import * as echarts from 'echarts';
import { AbmBarData, AbmLineData, AbmListData, AbmScatterData, BarType, NameValue } from '../global/types';
import { AbmUtil } from './abm-util';

export class AbmEchartDataUtil {

  public static getTitle(text: string, position = 'left', otherOptions?: any) {
    return {
      title: {
        text,
        left: position,
        ...otherOptions,
      }
    };
  }

  public static getToolTip(trigger: 'item' | 'axis', otherOptions?: any) {
    return {
      tooltip: {
        trigger,
        ...otherOptions,
      }
    };
  }

  public static getLegend(legends: string[], orient: 'vertical' | 'horizontal' = 'horizontal', align: 'left' | 'right' = 'left', left?: string, top?: string, otherOptions?: any) {
    return {
      legend: {
        orient,
        left,
        top,
        align,
        data: legends,
        ...otherOptions,
      }
    };
  }

  public static getGrid(grid?: [string, string, string, string], otherOptions?: any) {
    if (!grid) {
      grid = ['10%', '10%', '10%', '10%'];
    }

    return {
      grid: {
        top: grid[0],
        right: grid[1],
        bottom: grid[2],
        left: grid[3],
        ...otherOptions,
      }
    };
  }

  public static getPieOptions(inputData: NameValue[], title: string = '', radius?: [string, string], center?: [string, string]): any {
    return {
      title: {
        text: title,
        left: 'right',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: radius ?? ['55%', '70%'],
          avoidLabelOverlap: true,
          // startAngle: 180,
          center: center ?? ['50%', '50%'],
          label: {
            show: true,
          },
          emphasis: {
            label: {
              show: true,
              textStyle: {
                fontSize: '20',
                fontWeight: 'bold'
              }
            }
          },
          data: inputData
        }
      ]
    };
  }

  public static getPieSeries(inputData: NameValue[], title: string = '', radius?: [string, string], center?: [string, string]) {
    return {
      name: title,
      type: 'pie',
      radius: radius ? radius : ['55%', '70%'],
      center,
      avoidLabelOverlap: false,
      // startAngle: 180,
      label: {
        show: false,
      },
      emphasis: {
        label: {
          show: true,
          textStyle: {
            fontSize: '30',
            fontWeight: 'bold'
          }
        }
      },
      data: inputData
    };
  }

  public static getBarOptions(type: BarType, inputData: AbmBarData): any {
    switch (type) {
      case BarType.BAR_SINGLE_Y_AXIS:
        return this._getBarSingleYaxisOptions(inputData);
      case BarType.BAR_DOUBLE_Y_AXIS:
        return this._getBarDoubleYaxisOptions(inputData);
      case BarType.BAR_ON_X_AXIS:
        return this._getBarsOnXaxis(inputData);
      default:
        break;
    }
  }

  public static getLineOptions(inputData: AbmLineData, title: string = '', Yunit: string = '', titlePos = 'left'): any {
    const allLines = [];
    for (let i = 0; i < inputData.length; i++) {
      const data = {
        name: inputData.legend[i],
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        data: inputData.data[i]
      };
      allLines.push(data);
    }

    allLines[0].areaStyle = {
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [{
          offset: 0, color: 'yellow' // color at 0% position
        }, {
          offset: 1, color: 'rgba(0,0,0,0)' // color at 100% position
        }],
        global: false // false by default
      }
    };

    return {
      title: {
        text: title,
        x: titlePos,
        y: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal',
        },
      },
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: inputData.legend,
        left: 'right',
        top: 'top',
        padding: [10, 20, 0, 0],
        orient: 'horizontal'
      },
      grid: {
        top: '16%',
        left: '2%',
        right: '5%',
        bottom: '4%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: inputData.xAxis,
        splitLine: {
          show: false
        },
      },
      yAxis: {
        name: Yunit,
        type: 'value',
        splitLine: {
          lineStyle: {
            color: 'rgba(128,128,128,0.4)',
          },
          show: true,
        },
      },
      series: allLines
    };
  }

  public static getBarWithLine(legend: NameValue[], xAxisData: string[], barData: number[], lineData: number[]): any {
    const legendData = legend.map(l => l.name);
    return {
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: legendData
      },
      xAxis: [
        {
          type: 'category',
          data: xAxisData,
          axisPointer: {
            type: 'shadow'
          },
          splitLine: {
            show: false,
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: legend[0].value,
          axisLabel: {
            formatter: '{value}'
          },
          splitLine: {
            show: false,
          }
        },
        {
          type: 'value',
          name: legend[1].value,
          axisLabel: {
            formatter: '{value}'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(128,128,128,0.4)',
            },
            show: true,
          }
        }
      ],
      series: [
        {
          name: legend[0].name,
          type: 'bar',
          data: barData,
          barWidth: '40%'
        },
        {
          name: legend[1].name,
          type: 'line',
          yAxisIndex: 1,
          data: lineData,
        }
      ]
    };
  }

  public static getScatterOptions(inputData: AbmScatterData[], sizeRatio: number, title: string = ''): any {

    const data = inputData.map(d => d.unzip());
    return {
      title: {
        text: title
      },
      grid: {
        top: '19%',
        left: '12%',
        right: '10%',
        bottom: '15%',
      },
      xAxis: {
        name: '人',
        splitLine: {
          lineStyle: {
            color: 'rgba(128,128,128,0.4)',
            type: 'dashed'
          }
        },
        min: Math.round(Math.min(...(inputData.map(i => i.x as number))) * 0.99),
        max: Math.round(Math.max(...(inputData.map(i => i.x as number)))),
      },
      yAxis: {
        name: 'kWh',
        splitLine: {
          lineStyle: {
            color: 'rgba(128,128,128,0.4)',
            type: 'dashed'
          }
        },
        min: Math.round(Math.min(...(inputData.map(i => i.y as number))) * 0.98),
        max: Math.round(Math.max(...(inputData.map(i => i.y as number))) * 1.01),
      },
      series: [{
        data,
        type: 'scatter',
        symbolSize: (d) => {
          return Math.sqrt(d[2]) / sizeRatio;
        },
        label: {
          show: false,
          formatter: (param) => {
            return param.data[3];
          },
          position: 'top',
          color: 'white'
        },
        emphasis: {
          label: {
            show: true,
            formatter: (param) => {
              return param.data[3];
            },
            position: 'top',
            color: 'white'
          }
        },
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(120, 36, 50, 0.5)',
          shadowOffsetY: 5,
          color: new echarts.graphic.RadialGradient(0.4, 0.3, 1, [{
            offset: 0,
            color: 'rgb(251, 118, 123)'
          }, {
            offset: 1,
            color: 'rgb(204, 46, 72)'
          }])
        }
      }]
    };
  }

  private static _getBarSingleYaxisOptions(inputData: AbmBarData): any {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: {
          type: 'line'
        }
      },
      grid: {
        top: '2%',
        bottom: '2%',
        left: '2%',
        right: '20%',
        containLabel: true
      },
      xAxis: [
        {
          type: 'value',
          axisLabel: {
            show: false,
            interval: 1,
            textStyle: {
              color: '#00bbff',
              fontSize: 12
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          }

        }
      ],
      yAxis: {
        type: 'category',
        data: inputData.categories,
        inverse: true,
        axisLabel: {
          color: 'rgba(255,255,255,1)',
        },
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        },
        splitLine: {
          show: false
        },
        zlevel: 99
      },
      series: [{
        type: 'bar',
        barWidth: '40%',
        itemStyle: {
          normal: {
            barBorderRadius: [5, 5, 5, 5],
            // color: new echarts.graphic.LinearGradient(
            //   0, 0, 1, 0,
            //   [
            //     { offset: 0, color: '#00EEF1' },
            //     { offset: 0.5, color: '#048BFE' },
            //     { offset: 1, color: '#00BBFF' }
            //   ]
            // ),
            label: {
              show: true,
              position: 'right',
              textStyle: {
                color: '#fff',
                fontSize: '14'
              }
            }
          }
        },
        data: inputData.data[0]
      }]
    };
  }

  private static _getBarDoubleYaxisOptions(inputData: AbmBarData): any {

    const formatter = (a) => {
      const v = a[0];
      return v.name + '<br/>' + v.marker + v.seriesName + '：' + Math.abs(v.value);
    };

    return {
      backgroundColor: 'transparent',
      legend: { data: inputData.legend, padding: [15, 5, 5, 5] },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        // 自定义提示内容
        formatter,
      },
      xAxis: [{
        type: 'value',
        inverse: true,
        gridIndex: 0,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: {
          show: false,
          lineStyle: {
            color: '#000',
          }
        },
        splitLine: {
          show: false
        }
      },
      {
        gridIndex: 1,
        show: false,
      },
      {
        type: 'value',
        gridIndex: 2,
        axisTick: { show: false }, // 是否显示刻度
        axisLine: {
          show: false, // 是否显示X轴
          lineStyle: {
            color: '#000',
          }
        },
        axisLabel: {
          show: false // 是否显示X轴内容
        },
        splitLine: {
          show: false
        }
      }],
      yAxis: [{
        type: 'category',
        inverse: true,
        position: 'right',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false,
        },
        data: inputData.categories,
      }, {
        gridIndex: 1,
        type: 'category',
        inverse: true,
        position: 'right',
        offset: -8,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          show: true,
          textStyle: {
            color: '#ffffff',
            fontSize: 12,
          },

        },
        data: inputData.categories.map((v) => {
          return {
            value: v,
            textStyle: {
              align: 'center',
            }
          };
        }),
      }, {
        gridIndex: 2,
        type: 'category',
        inverse: true,
        position: 'left',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false,
          textStyle: {
            color: '#9D9EA0',
            fontSize: 12,
          },
        },
        data: inputData.categories,
      }],
      grid: [{
        show: false,
        left: '0%',
        top: 30,
        bottom: 30,
        containLabel: true,
        width: '29%',
      }, {
        show: false,
        left: '50%',
        top: 30,
        bottom: 30,
        width: '0%',
      }, {
        show: false,
        right: '0%',
        top: 30,
        bottom: 30,
        containLabel: true,
        width: '29%',
      }],
      // color: ['#2FACFA', '#F5A623'],
      series: [
        {
          name: inputData.legend[0],
          type: 'bar',
          barWidth: '10',
          gridIndex: 0,
          itemStyle: {
            normal: {
              show: true,
              color: '#0f94c6',
              barBorderRadius: 50,
              // borderWidth: 0,
              // borderColor: '#333',
              label: {
                show: true, position: 'right',
                formatter: (v) => {
                  return v.value;
                }
              }
            }
          },
          data: inputData.data[0]
        },
        {
          name: inputData.legend[1],
          type: 'bar',
          barWidth: '10',
          xAxisIndex: 2,
          yAxisIndex: 2,
          itemStyle: {
            normal: {
              show: true,
              color: '#8FBF73',
              barBorderRadius: 50,
              // borderWidth: 0,
              // borderColor: '#333',
              label: {
                show: true, position: 'left',
                formatter: (v) => {
                  return v.value;
                }
              }
            }
          },
          data: inputData.data[1]
        }

      ]
    };

  }

  private static _getBarsOnXaxis(inputData: AbmBarData): any {

    const series = inputData.data.map((d, i) => {
      return {
        name: inputData.legend?.[i],
        type: 'bar',
        data: d,
      };
    });

    return {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: inputData.legend
      },
      calculable: true,
      xAxis: [
        {
          type: 'category',
          triggerEvent: true,
          data: inputData.categories,
          boundaryGap: true,
          splitLine: {
            show: false,
          },
          // splitArea: {
          //   show: true,
          //   areaStyle: {
          //     color: ['rgba(250,250,250,0)', 'rgba(252,98,64,0.2)'],
          //   }
          // },
        }
      ],
      yAxis: [
        {
          type: 'value',
          splitLine: {
            show: false,
          }
        }
      ],
      series,
    };
  }


  public buildListOptions(data: any[], listFields: string[], headers?: string[]): AbmListData {

    const values = data.map(d => {
      const a: any[] = [];
      for (const field of listFields) {
        if (d.hasOwnProperty(field)) {
          a.push(d[field]);
        } else if (field.includes('-')) {
          const each = field.split('-');
          for (const f of each) {
            if (!d.hasOwnProperty(f)) {
              throw new Error(f + ' is not property of data');
            }
          }
          if (each.length === 2) {
            a.push(AbmUtil.dashStr(d[each[0]], d[each[1]]));
          } else {
            throw new Error('More than 2 fields connect not supported yet');
          }
        } else {
          throw new Error(field + ' is not property of data');
        }
      }
      return a;
    });

    return new AbmListData(values, headers);
  }

  public buildLineOptions(
    data: any[],
    valueFields: string[],
    legend: string[],
    xField: string,
    xOperator?: (s: string) => string, title?: string, Yunit?: string): any {

    const xAxis = [];
    const values = new Array<any>(valueFields.length);

    AbmUtil.checkProperties(data[0], ...valueFields, xField);

    data.forEach(d => {
      if (xOperator) {
        xAxis.push(xOperator(d[xField] as string));
      } else {
        xAxis.push(d[xField] as string);
      }

      for (let i = 0; i < valueFields.length; i++) {
        const field = valueFields[i];
        if (!values[i]) {
          values[i] = new Array<any>();
        }
        values[i].push(d[field]);
      }
    });
    return AbmEchartDataUtil.getLineOptions(new AbmLineData(legend, values, xAxis), title, Yunit);
  }

  public buildPieOptions(data: any[], categoryField: string, valueField: string): any {

    AbmUtil.checkProperties(data[0], categoryField, valueField);

    const values: NameValue[] = data.map(d => {
      return new NameValue(d[categoryField], d[valueField]);
    });

    return AbmEchartDataUtil.getPieOptions(values);
  }

  public buildBarOptions(
    barType: BarType,
    data: any[],
    valueFields: string[],
    categoryField1: string,
    categoryField2?: string,
    legend?: string[],
    categoryOperator?: (...s: string[]) => string): any {

    const categories = [];
    const values = new Array<any>(valueFields.length);

    AbmUtil.checkProperties(data[0], categoryField1, categoryField2, ...valueFields);

    data.forEach(d => {

      if (categoryField2) {
        if (categoryOperator) {
          categories.push(categoryOperator(d[categoryField1], d[categoryField2]));
        } else {
          categories.push(d[categoryField1] + ' ' + d[categoryField2]);
        }
      } else {
        categories.push(d[categoryField1]);
      }

      for (let i = 0; i < valueFields.length; i++) {
        const field = valueFields[i];
        if (!values[i]) {
          values[i] = new Array<any>();
        }
        values[i].push(d[field]);
      }
    });

    return AbmEchartDataUtil.getBarOptions(barType, new AbmBarData(categories, values, legend));
  }

  public lineColumnDataToRow(data: any[], columnField: string, valueField: string, xField: string): { valueFields: string[], data: any[] } {

    AbmUtil.checkProperties(data[0], columnField, valueField, xField);

    const resultHeader: Set<string> = new Set();
    const resultData: any[] = [];

    data.forEach(d => {
      const newFieldName = d[columnField];
      resultHeader.add(newFieldName);
      if (resultData.length === 0) {
        resultData.push({ [xField]: d[xField], [newFieldName]: d[valueField] });
      } else {
        const one = AbmUtil.findInArray(resultData, xField, d[xField]);
        if (one === undefined) {
          resultData.push({ [xField]: d[xField], [newFieldName]: d[valueField] });
        } else {
          one[newFieldName] = d[valueField];
        }
      }
    });
    return { valueFields: Array.from(resultHeader.values()), data: resultData };
  }

}
